import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Download, Mail, Check, Loader2 } from 'lucide-react';
import { API_URL } from '@/network/http';
import http from '@/network/http';
import { useAuthStore } from '@/features/auth';

const ANDROID_VERSION = __ANDROID_VERSION__;
const IOS_VERSION = __IOS_VERSION__;
const APK_URL = `${API_URL}/uploads/file/releases/android/bw-report-${ANDROID_VERSION}.apk`;
const TESTFLIGHT_URL = 'https://testflight.apple.com/join/3pP3dDvA';

export default function DownloadsPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [email, setEmail] = useState(user?.email ?? '');
  const [inviteStatus, setInviteStatus] = useState<
    'idle' | 'loading' | 'success' | 'already' | 'error'
  >('idle');

  const handleInvite = async () => {
    if (!email) return;
    setInviteStatus('loading');
    try {
      const { data } = await http.post('/testflight/invite', {
        email,
        firstName: user?.firstName,
        lastName: user?.lastName,
      });
      setInviteStatus(data.alreadyInvited ? 'already' : 'success');
    } catch {
      setInviteStatus('error');
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">
        {t('downloads.title')}
      </h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Android card */}
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-50">
            <Smartphone className="h-7 w-7 text-green-600" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('downloads.android')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('downloads.version', { version: ANDROID_VERSION })}
            </p>
          </div>
          <a
            href={APK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 active:scale-[0.98]"
          >
            <Download className="h-4 w-4" />
            {t('downloads.downloadApk')}
          </a>
        </div>

        {/* iOS card */}
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-50">
            <Smartphone className="h-7 w-7 text-blue-600" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('downloads.ios')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('downloads.version', { version: IOS_VERSION })}
            </p>
          </div>

          {/* TestFlight invitation form */}
          <div className="flex w-full flex-col gap-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (inviteStatus !== 'idle' && inviteStatus !== 'loading')
                      setInviteStatus('idle');
                  }}
                  placeholder={t('downloads.emailPlaceholder')}
                  className="w-full rounded-xl border border-gray-200 py-2.5 ps-9 pe-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  dir="ltr"
                />
              </div>
              <button
                onClick={handleInvite}
                disabled={!email || inviteStatus === 'loading'}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50"
              >
                {inviteStatus === 'loading' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : inviteStatus === 'success' || inviteStatus === 'already' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                {t('downloads.sendInvite')}
              </button>
            </div>

            {inviteStatus === 'success' && (
              <p className="text-center text-sm text-green-600">
                {t('downloads.inviteSent')}
              </p>
            )}
            {inviteStatus === 'already' && (
              <p className="text-center text-sm text-blue-600">
                {t('downloads.alreadyInvited')}
              </p>
            )}
            {inviteStatus === 'error' && (
              <p className="text-center text-sm text-red-600">
                {t('downloads.inviteError')}
              </p>
            )}
          </div>

          <a
            href={TESTFLIGHT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-[0.98]"
          >
            <Download className="h-4 w-4" />
            {t('downloads.downloadTestflight')}
          </a>
        </div>
      </div>

      <p className="mt-6 text-sm text-gray-500">
        {t('downloads.installInstructions')}
      </p>
    </div>
  );
}
